import { Button } from '@approvalmax/ui';
import { Box, Flex, Popup, Text } from '@approvalmax/ui/src/components';
import { intl } from '@approvalmax/utils';
import { useAtomValue, useSetAtom } from 'jotai';
import ImageAttention from 'modules/components/ImageIcons/ImageAttention';
import { SectionContainer } from 'pages/requestForm/components/SectionContainer';
import { memo, useCallback } from 'react';
import { useToggle } from 'react-use';
import { notificationService } from 'services/notification';
import { netSuiteBillPaymentApplyListState, netSuiteBillPaymentFieldValuesState } from 'shared/states';

import { NetSuiteApplyList } from '../../../components/NetSuiteApplyList/NetSuiteApplyList';
import { NetSuiteCreditList } from '../../../components/NetSuiteCreditList/NetSuiteCreditList';
import {
    useNetSuiteBillPaymentAmount,
    useNetSuiteBillPaymentClearItems,
} from '../../NetSuiteBillPaymentRequestForm.hooks';
import {
    netSuiteBillPaymentCreditListState,
    netSuiteBillPaymentShowPopupErrorsState,
} from '../../NetSuiteBillPaymentRequestForm.states';
import { AddBillsPopup } from './components/AddBillsPopup/AddBillsPopup';
import { messages } from './NetSuiteBillPaymentBillsSection.messages';
import { AmountValue, RemoveAllBillsButton } from './NetSuiteBillPaymentBillsSection.styles';
import { NetSuiteBillPaymentBillsSectionProps } from './NetSuiteBillPaymentBillsSection.types';

export const NetSuiteBillPaymentBillsSection = memo((props: NetSuiteBillPaymentBillsSectionProps) => {
    const { request, currency, accountId, payee } = props;

    const fieldValues = useAtomValue(netSuiteBillPaymentFieldValuesState);
    const applyList = useAtomValue(netSuiteBillPaymentApplyListState);
    const creditList = useAtomValue(netSuiteBillPaymentCreditListState);
    const setShowPopupErrors = useSetAtom(netSuiteBillPaymentShowPopupErrorsState);

    const [isAddBillsPopupOpen, toggleIsAddBillsPopupOpen] = useToggle(false);

    const amount = useNetSuiteBillPaymentAmount(currency);

    const clearItems = useNetSuiteBillPaymentClearItems();

    const openAddBillsPopup = useCallback(() => {
        if (!payee || !currency || !accountId) {
            setShowPopupErrors(true);
            notificationService.showErrorToast(messages.missingValuesWarning);

            return;
        }

        setShowPopupErrors(false);
        toggleIsAddBillsPopupOpen();
    }, [payee, currency, accountId, toggleIsAddBillsPopupOpen, setShowPopupErrors]);

    return (
        <>
            <SectionContainer>
                <Box spacing='25 0 15'>
                    <Flex justifyContent='space-between'>
                        <Text font='body' fontWeight='bold' uppercase>
                            {messages.numberOfBills({ quantity: applyList.length })}
                        </Text>

                        <RemoveAllBillsButton link color='red70' onClick={clearItems}>
                            {messages.removeAllBillsButtonText}
                        </RemoveAllBillsButton>
                    </Flex>
                </Box>

                <Box spacing='25 0 15'>
                    <Flex alignItems='center' justifyContent='space-between'>
                        <Button execute={openAddBillsPopup}>Add Bills</Button>

                        <Flex direction='column' alignItems='end' spacing='0'>
                            <Text font='body' fontWeight='bold' color='midnight70'>
                                {messages.totalAmountText}
                            </Text>

                            <AmountValue font='body' fontWeight='bold' color='black100'>
                                {intl.formatCurrency(amount, fieldValues.currency?.text || '', 2, false)}
                            </AmountValue>
                        </Flex>
                    </Flex>
                </Box>

                {applyList.length === 0 && creditList.length === 0 && (
                    <Box spacing='10 0 0'>
                        <Flex direction='column' alignItems='center' justifyContent='space-between' spacing='20 0'>
                            <ImageAttention />

                            <Text font='body' fontWeight='medium' fontSize='medium'>
                                {messages.noBillsText}
                            </Text>
                        </Flex>
                    </Box>
                )}
            </SectionContainer>

            {Boolean(applyList.length) && (
                <SectionContainer>
                    <NetSuiteApplyList />
                </SectionContainer>
            )}

            {Boolean(creditList.length) && (
                <SectionContainer>
                    <NetSuiteCreditList />
                </SectionContainer>
            )}

            {currency && accountId && payee && (
                <Popup open={isAddBillsPopupOpen} onToggle={toggleIsAddBillsPopupOpen} size='large'>
                    <AddBillsPopup
                        payeeId={payee.id}
                        payeeType={payee.type}
                        companyId={request.companyId}
                        billPaymentRequestId={request.id}
                        currency={currency}
                        accountId={accountId}
                        togglePopup={toggleIsAddBillsPopupOpen}
                    />
                </Popup>
            )}
        </>
    );
});

NetSuiteBillPaymentBillsSection.displayName = 'NetSuiteBillPaymentBillsSection';
